import React, { useEffect, useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory, { PaginationProvider }  from "react-bootstrap-table2-paginator";
import { getAuthorities } from "../../../../../../_helpers/Helpers";
import * as actions from "../../../_redux/students/StudentsAction";
import * as uiHelpers from "../StudentsUIHelpers";
import { 
  getSelectRow, 
  getHandlerTableChange,  
  NoRecordsFoundMessage, 
  PleaseWaitMessage, 
  sortCaret
} from "../../../../../../_metronic/_helpers";
import * as columnFormatters from "./column-formatters";
import { Pagination } from "../../../../../../_metronic/_partials/controls";
import { useStudentsUIContext } from "../StudentsUIContext";

export function StudentsTable() {
  // Students UI Context
  const studentsUIContext = useStudentsUIContext();
  const studentsUIProps = useMemo(() => {
    return {
      ids: studentsUIContext.ids,
      setIds: studentsUIContext.setIds,
      queryParams: studentsUIContext.queryParams,
      setQueryParams: studentsUIContext.setQueryParams,
      openEditStudentPage: studentsUIContext.openEditStudentPage,
      openRegistrationCancellationModal: studentsUIContext.openRegistrationCancellationModal
    };
  }, [studentsUIContext]);

  // Getting curret state of Students list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.students }),
    shallowEqual
  );
  const { totalCount, entities, authorities, listLoading } = currentState;
  
  // Authority Parameters
  const { isEditionAuthorized, isDeletionAuthorized } = getAuthorities(authorities);  
  
  // Students Redux state
  const dispatch = useDispatch();

  useEffect(() => {
    studentsUIProps.setIds([]);
    dispatch(actions.getStudents(studentsUIProps.queryParams));
    return () => dispatch(actions.cleanStudentsState());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentsUIProps.queryParams, dispatch]);

  // Table columns
  const columns = [
    {
      dataField: "photoStr",
      text: "Fotoğraf",
      formatter: columnFormatters.PhotoColumnFormatter,
      style: {
        minWidth: "100px"
      }
    },
    {
      dataField: "registrationDate",
      text: "Kayıt Tarihi",
      sort: true,
      sortCaret: sortCaret,
    },
    {
      dataField: "studentNumber",
      text: "Öğrenci Numarası",
      sort: true,
      sortCaret: sortCaret,
    },
    {
      dataField: "identityCard.tcKimlikNo",
      text: "TC Kimlik No",
      sort: true,
      sortCaret: sortCaret,
    },
    {
      dataField: "identityCard.name",
      text: "Adı",
      sort: true,
      sortCaret: sortCaret,
    },    
    {
      dataField: "identityCard.surname",
      text: "Soyadı",
      sort: true,
      sortCaret: sortCaret,
    },
    {
      dataField: "sexName",
      text: "Cinsiyeti",
      sort: true,
      sortCaret: sortCaret,
    },
    {
      dataField: "section",
      text: "Şube",
      sort: true,
      sortCaret: sortCaret,
    }
  ];

  const actionColumn = {
    dataField: "action",
    text: "İşlemler",
    formatter: columnFormatters.ActionsColumnFormatter,
    formatExtraData: {
      term: studentsUIProps.queryParams.term,
      isEditionAuthorized,
      isDeletionAuthorized,
      openEditStudentPage: studentsUIProps.openEditStudentPage,
      openRegistrationCancellationModal: studentsUIProps.openRegistrationCancellationModal
    },
    classes: "text-right pr-0",
    headerClasses: "text-right pr-16",
    style: {
      width: "180px",
    }
  }

  if (isEditionAuthorized || isDeletionAuthorized) columns.push(actionColumn);

  // Table pagination properties
  const paginationOptions = {
    custom: true,
    totalSize: totalCount,
    sizePerPageList: uiHelpers.sizePerPageList,
    sizePerPage: studentsUIProps.queryParams.pageSize,
    page: studentsUIProps.queryParams.pageNumber
  };
  
  return (
    <PaginationProvider pagination={paginationFactory(paginationOptions)}>
      {({ paginationProps, paginationTableProps }) => {
        return (
          <Pagination
            isLoading={listLoading}
            paginationProps={paginationProps}
          >
            <BootstrapTable
              wrapperClasses="table-responsive"
              classes="table table-head-custom table-vertical-center overflow-hidden"
              bootstrap4
              bordered={false}
              remote
              keyField="id"
              data={entities ?? [] }
              columns={columns}
              defaultSorted={uiHelpers.defaultSorted}
              onTableChange={getHandlerTableChange(
                studentsUIProps.setQueryParams
              )}
              selectRow={getSelectRow({
                entities,
                ids: studentsUIProps.ids,
                setIds: studentsUIProps.setIds,
                hidden: !isDeletionAuthorized
              })}
              {...paginationTableProps}
            >
              <PleaseWaitMessage entities={entities} />
              <NoRecordsFoundMessage entities={entities} />
            </BootstrapTable>
          </Pagination>
        );    
      }}
    </PaginationProvider>  
  );
}